/**
 * Investigation step form (Module 24).
 * Small dialog form for adding a step to an investigation.
 */

import { useMemo, useState } from 'react';
import { Button, CoordioDropdown, Input, Textarea } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { investigationService } from '../../services/investigationService';
import { useNotificationStore } from '../../store/notificationStore';
import {
  InvestigationStepTypes,
  type InvestigationStepType,
  type AddInvestigationStepRequest,
} from '../../types/investigation';

export interface InvestigationStepFormProps {
  open: boolean;
  investigationId: string;
  defaultOrder?: number;
  onClose: () => void;
  onCreated?: () => void;
}

const stepTypeLabel = (type: InvestigationStepType): string => {
  switch (type) {
    case InvestigationStepTypes.DataCollection:
      return 'Data Collection';
    case InvestigationStepTypes.Analysis:
      return 'Analysis';
    case InvestigationStepTypes.Hypothesis:
      return 'Hypothesis';
    case InvestigationStepTypes.Verification:
      return 'Verification';
    case InvestigationStepTypes.Conclusion:
      return 'Conclusion';
    default:
      return 'Unknown';
  }
};

/**
 * Dialog form for adding a new investigation step.
 */
export const InvestigationStepForm = ({
  open,
  investigationId,
  defaultOrder,
  onClose,
  onCreated,
}: InvestigationStepFormProps) => {
  const { showNotification } = useNotificationStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stepName, setStepName] = useState('');
  const [description, setDescription] = useState('');
  const [stepType, setStepType] = useState<InvestigationStepType>(InvestigationStepTypes.DataCollection);
  const [order, setOrder] = useState<number>(defaultOrder ?? 1);

  const stepTypeOptions = useMemo(
    () => [
      InvestigationStepTypes.DataCollection,
      InvestigationStepTypes.Analysis,
      InvestigationStepTypes.Hypothesis,
      InvestigationStepTypes.Verification,
      InvestigationStepTypes.Conclusion,
    ],
    []
  );

  const handleClose = () => {
    if (saving) return;
    setError(null);
    onClose();
  };

  const handleCreate = async () => {
    setError(null);
    if (!stepName.trim()) {
      setError('Step name is required.');
      return;
    }
    if (!Number.isFinite(order) || order <= 0) {
      setError('Order must be a positive number.');
      return;
    }

    setSaving(true);
    try {
      const request: AddInvestigationStepRequest = {
        stepName: stepName.trim(),
        description: description.trim() || undefined,
        stepType,
        order,
      };

      await investigationService.addInvestigationStep(investigationId, request);
      showNotification('Step added successfully', 'success');
      setStepName('');
      setDescription('');
      setStepType(InvestigationStepTypes.DataCollection);
      setOrder(defaultOrder ?? 1);
      onCreated?.();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to add step';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CortexDialog open={open} onClose={handleClose} title="Add Investigation Step" size="lg" data-testid="investigation-step-form-dialog">
      <CortexDialogBody>
        {error && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400 mb-3">
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        <div className="grid gap-2 mt-1">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Step Name</label>
            <Input className="w-full" value={stepName} onChange={(e) => setStepName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Description</label>
            <Textarea className="w-full" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Step Type</label>
            <CoordioDropdown
              options={stepTypeOptions.map((t) => ({ label: stepTypeLabel(t), value: String(t) }))}
              value={String(stepType)}
              onChange={(v) => setStepType(Number(v) as InvestigationStepType)}
              clearable={false}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Order</label>
            <Input className="w-full" type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} min={1} />
          </div>
        </div>
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="ghost" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={saving}>
          {saving ? 'Adding…' : 'Add Step'}
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
